/**
 * AdminInput — labelled text field for the admin portal, matching /brand
 * Aspect 13 (Form field). Replaces the bespoke `DarkInput` re-implemented
 * across ~8 admin screens. Single- or multi-line, with an uppercase
 * eyebrow label and focus ring. Theme-driven — no hex.
 */
import { useState } from "react";
import {
  Platform,
  Text,
  TextInput,
  View,
  type TextStyle,
  type ViewStyle,
} from "react-native";

import { RADII, STROKES } from "../../../lib/brand";
import type { PaneTheme } from "../../brand/paneThemes";

export type AdminInputProps = {
  theme: PaneTheme;
  label?: string;
  value: string;
  onChangeText: (v: string) => void;
  placeholder?: string;
  multiline?: boolean;
  /** Multiline height; ignored when single-line. */
  minHeight?: number;
  keyboardType?: "default" | "email-address" | "numeric" | "number-pad";
  autoCapitalize?: "none" | "sentences" | "words" | "characters";
  editable?: boolean;
  /** Optional hint rendered under the field. */
  hint?: string;
  style?: ViewStyle;
};

export function AdminInput({
  theme,
  label,
  value,
  onChangeText,
  placeholder,
  multiline = false,
  minHeight = 96,
  keyboardType = "default",
  autoCapitalize = "sentences",
  editable = true,
  hint,
  style,
}: AdminInputProps) {
  const [focused, setFocused] = useState(false);
  const isWeb = Platform.OS === "web";

  return (
    <View style={[{ gap: 6 }, style ?? null]}>
      {label ? (
        <Text
          style={{
            fontFamily: theme.bodySemiBoldFont,
            fontSize: 10,
            letterSpacing: 2.2,
            textTransform: "uppercase",
            color: theme.textOnSurfaceFaint,
          }}
        >
          {label}
        </Text>
      ) : null}
      <TextInput
        value={value}
        onChangeText={onChangeText}
        placeholder={placeholder}
        placeholderTextColor={theme.textOnSurfaceFaint}
        multiline={multiline}
        keyboardType={keyboardType}
        autoCapitalize={autoCapitalize}
        editable={editable}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={[
          {
            borderWidth: STROKES.hairline,
            borderColor: focused ? theme.accentBorderHover : theme.borderStrong,
            borderRadius: RADII.sm,
            paddingHorizontal: 12,
            paddingVertical: 10,
            backgroundColor: theme.surfaceRaisedBg,
            color: editable ? theme.textOnSurface : theme.textOnSurfaceDim,
            fontFamily: theme.bodyFont,
            fontSize: 13,
            ...(multiline
              ? { minHeight, textAlignVertical: "top" as const }
              : null),
          } as TextStyle,
          isWeb ? ({ outlineStyle: "none" } as unknown as TextStyle) : null,
        ]}
      />
      {hint ? (
        <Text
          style={{
            fontFamily: theme.bodyFont,
            fontSize: 11,
            color: theme.textOnSurfaceFaint,
          }}
        >
          {hint}
        </Text>
      ) : null}
    </View>
  );
}
